import React, { useState, useEffect } from "react";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";
import moment from "moment";
import XLSX from "xlsx";
import IconButton from "@material-ui/core/IconButton";

import ReceiptLongIcon from "@material-ui/icons/Receipt";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import ReplayIcon from "@material-ui/icons/Replay";
import RefreshIcon from "@material-ui/icons/Refresh";
import CloudDownloadIcon from "@material-ui/icons/CloudDownload";
import ChevronRightIcon from "@material-ui/icons/ChevronRight";
import FileCopyIcon from "@material-ui/icons/FileCopy";

import ErrorPage from "commonComponents/errorPage/ErrorPage";
import ShopTransactionDetails from "./ShopTransactionDetails";
import DialogShell from "designV2/components/DialogShell";
import PageHeader from "designV2/components/PageHeader";
import PageActionButton from "designV2/components/PageActionButton";
import MetricCard from "designV2/components/MetricCard";
import SearchField from "designV2/components/SearchField";
import FilterDropdown from "designV2/components/FilterDropdown";
import NumberRangeFilter from "designV2/components/NumberRangeFilter";
import DateRangeFilter from "designV2/components/DateRangeFilter";
import ActiveFilterChipList from "designV2/components/ActiveFilterChipList";
import StatusBadge from "designV2/components/StatusBadge";
import EmptyState from "designV2/components/EmptyState";
import PaginationComponent from "designV2/components/Pagination";
import { MetricsSkeleton, TableSkeleton } from "designV2/components/Skeleton";
import "./finance.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * Two gaps fixed here: the amount filter only ever matched an exact
 * value ($amount) even though the backend's GetTransactionQueryInput
 * also supports an $amountFrom range, and "reversed" was a real filter
 * field with no control for it at all. The "Type" dropdown that used
 * to sit here is gone, not just left unwired — GetTransactionQueryInput
 * genuinely has no `type` field, so there was nothing to fix it into.
 * ------------------------------------------------------------------ */

const TRANSACTION_FIELDS = `
  _id
  transactionId
  status
  paidAt
  type
  amount
  reversed
  reversalId
  refundId
  payments {
    _id
    trip {
      _id
    }
    order {
      _id
    }
    for
    status
    description
    createdAt
    updatedAt
    amount
    type
    shop {
      _id
      name
    }
    driver {
      _id
      fullName
    }
  }
  paymentIntent
  createdAt
  updatedAt
`;

const GET_TRANSACTIONS = gql`
  query getShopTransactions(
    $limit: Int
    $skip: Int
    $transactionId: String
    $status: PaymentStatus
    $reversed: Boolean
    $amount: Float
    $amountFrom: Float
    $paidAt: Date
    $paidAtFrom: Date
    $createdAt: Date
    $createdAtFrom: Date
    $createdAtSort: Int
    $paidAtSort: Int
    $amountSort: Int
  ) {
    getShopTransactions(
      pagination: { limit: $limit, skip: $skip }
      filters: {
        transactionId: $transactionId
        status: $status
        reversed: $reversed
        amount: $amount
        amountFrom: $amountFrom
        paidAt: $paidAt
        paidAtFrom: $paidAtFrom
        createdAt: $createdAt
        createdAtFrom: $createdAtFrom
      }
      sort: { createdAt: $createdAtSort, paidAt: $paidAtSort, amount: $amountSort }
    ) {
      ${TRANSACTION_FIELDS}
    }
    getShopTransactionsCount(
      filters: {
        transactionId: $transactionId
        status: $status
        reversed: $reversed
        amount: $amount
        amountFrom: $amountFrom
        paidAt: $paidAt
        paidAtFrom: $paidAtFrom
        createdAt: $createdAt
        createdAtFrom: $createdAtFrom
      }
    )
  }
`;

const GET_STATUS_COUNTS = gql`
  query getShopTransactionsStatusCounts {
    paid: getShopTransactionsCount(filters: { status: PAID })
    unpaid: getShopTransactionsCount(filters: { status: UNPAID })
    reversed: getShopTransactionsCount(filters: { reversed: true })
  }
`;

const STATUS_OPTIONS = [
  { value: "PAID", label: "Paid" },
  { value: "UNPAID", label: "Unpaid" },
  { value: "FAILED", label: "Failed" },
  { value: "FAILED_DUE_TO_REJECTION", label: "Failed (rejection)" },
];
const STATUS_LABELS = STATUS_OPTIONS.reduce((acc, o) => {
  acc[o.value] = o.label;
  return acc;
}, {});
const STATUS_TONES = {
  PAID: "success",
  UNPAID: "warning",
  FAILED: "danger",
  FAILED_DUE_TO_REJECTION: "danger",
};

const TYPE_LABELS = {
  PAY_TO_SPARK: "Pay to Spark",
  PAY_TO_SHOP: "Pay to shop",
  PAY_TO_DRIVER: "Pay to driver",
  REJECTION_PENALTY: "Rejection penalty",
};

const SORT_OPTIONS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "paidNewest", label: "Paid date, newest" },
  { value: "amountHigh", label: "Amount high-low" },
  { value: "amountLow", label: "Amount low-high" },
];

const FILTER_DEBOUNCE_MS = 350;

const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "");
const formatTime = (value) => (value ? moment(value).format("hh:mm A") : "");

const Transactions = () => {
  const [refFilter, setRefFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [reversedOnly, setReversedOnly] = useState(false);
  const [amountFrom, setAmountFrom] = useState("");
  const [amountTo, setAmountTo] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [paidFrom, setPaidFrom] = useState("");
  const [paidTo, setPaidTo] = useState("");
  const [sortOption, setSortOption] = useState("newest");

  const [pageCount, setPageCount] = useState(0);
  const [perPage, setPerPage] = useState(10);

  const [transactionDetail, setTransactionDetail] = useState(null);
  const [copiedId, setCopiedId] = useState("");
  const [exporting, setExporting] = useState(false);

  const [variables, setVariables] = useState({
    limit: perPage,
    skip: 0,
    createdAtSort: -1,
  });

  useEffect(() => {
    const handler = setTimeout(() => {
      setVariables((v) => ({
        ...v,
        skip: 0,
        transactionId: refFilter.trim() || undefined,
        status: statusFilter || undefined,
        reversed: reversedOnly ? true : undefined,
        amountFrom: amountFrom ? parseFloat(amountFrom) : undefined,
        amount: amountTo ? parseFloat(amountTo) : undefined,
        createdAtFrom: dateFrom || undefined,
        createdAt: dateTo || undefined,
        paidAtFrom: paidFrom || undefined,
        paidAt: paidTo || undefined,
        createdAtSort: sortOption === "oldest" ? 1 : sortOption === "newest" ? -1 : undefined,
        paidAtSort: sortOption === "paidNewest" ? -1 : undefined,
        amountSort: sortOption === "amountHigh" ? -1 : sortOption === "amountLow" ? 1 : undefined,
      }));
      setPageCount(0);
    }, FILTER_DEBOUNCE_MS);
    return () => clearTimeout(handler);
  }, [refFilter, statusFilter, reversedOnly, amountFrom, amountTo, dateFrom, dateTo, paidFrom, paidTo, sortOption]);

  useEffect(() => {
    setVariables((v) => ({ ...v, limit: perPage, skip: perPage * pageCount }));
  }, [perPage, pageCount]);

  const { data, loading, error, refetch } = useQuery(GET_TRANSACTIONS, {
    variables,
    fetchPolicy: "network-only",
  });

  const { data: statusCountsData } = useQuery(GET_STATUS_COUNTS, { fetchPolicy: "network-only" });

  const [fetchAllForExport, { data: exportData }] = useLazyQuery(GET_TRANSACTIONS, { fetchPolicy: "network-only" });

  const transactions = (data && data.getShopTransactions) || [];
  const totalCount = (data && data.getShopTransactionsCount) || 0;
  const pageAmount = transactions.reduce((sum, t) => sum + (t.amount || 0), 0);
  const counts = statusCountsData || {};

  /* ---------------- filter chips ---------------- */

  const activeFilterChips = [];
  if (refFilter.trim()) activeFilterChips.push({ id: "ref", label: `Reference: ${refFilter}` });
  if (statusFilter) activeFilterChips.push({ id: "status", label: `Status: ${STATUS_LABELS[statusFilter]}` });
  if (reversedOnly) activeFilterChips.push({ id: "reversed", label: "Reversed only" });
  if (amountFrom || amountTo) {
    activeFilterChips.push({ id: "amount", label: `Amount: $${amountFrom || "0"} to $${amountTo || "-"}` });
  }
  if (dateFrom || dateTo) {
    activeFilterChips.push({ id: "date", label: `Created: ${dateFrom || "-"} to ${dateTo || "-"}` });
  }
  if (paidFrom || paidTo) {
    activeFilterChips.push({ id: "paid", label: `Paid: ${paidFrom || "-"} to ${paidTo || "-"}` });
  }

  const clearFilter = (id) => {
    if (id === "ref") setRefFilter("");
    if (id === "status") setStatusFilter("");
    if (id === "reversed") setReversedOnly(false);
    if (id === "amount") {
      setAmountFrom("");
      setAmountTo("");
    }
    if (id === "date") {
      setDateFrom("");
      setDateTo("");
    }
    if (id === "paid") {
      setPaidFrom("");
      setPaidTo("");
    }
  };

  const clearAllFilters = () => {
    setRefFilter("");
    setStatusFilter("");
    setReversedOnly(false);
    setAmountFrom("");
    setAmountTo("");
    setDateFrom("");
    setDateTo("");
    setPaidFrom("");
    setPaidTo("");
  };

  /* ---------------- export ---------------- */

  useEffect(() => {
    if (!exporting || !exportData) return;
    const rows = [["Reference", "Type", "Status", "Reversed", "Amount", "Paid at", "Created"]];
    exportData.getShopTransactions.forEach((t) => {
      rows.push([
        t.transactionId || "-",
        TYPE_LABELS[t.type] || t.type || "-",
        STATUS_LABELS[t.status] || t.status,
        t.reversed ? "Yes" : "No",
        Number(t.amount || 0).toFixed(2),
        t.paidAt ? moment(t.paidAt).format("MM/DD/YYYY HH:mm") : "-",
        moment(t.createdAt).format("MM/DD/YYYY HH:mm"),
      ]);
    });
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, ws, "Transactions");
    XLSX.writeFile(wb, "Transactions.xlsx");
    setExporting(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exportData]);

  const handleExport = () => {
    if (totalCount === 0 || exporting) return;
    setExporting(true);
    fetchAllForExport({ variables: { ...variables, skip: 0, limit: totalCount } });
  };

  const handleCopy = (id) => {
    if (!navigator.clipboard || !id) return;
    navigator.clipboard.writeText(id).then(() => {
      setCopiedId(id);
      window.setTimeout(() => setCopiedId(""), 1500);
    });
  };

  /* ---------------- render ---------------- */

  if (loading && !data) {
    return (
      <div className="page financePage transactionsPage">
        <main className="main">
          <PageHeader title="Transactions" description="Payout batches made up of one or more payments." />
          <MetricsSkeleton count={4} />
          <TableSkeleton rows={6} columns={6} />
        </main>
      </div>
    );
  }

  if (error) {
    return <ErrorPage />;
  }

  return (
    <div className="page financePage transactionsPage">
      <main className="main">
        <PageHeader
          title="Transactions"
          count={totalCount}
          description="Payout batches made up of one or more payments, and whether they've settled."
          actions={
            <PageActionButton
              icon={<CloudDownloadIcon fontSize="small" />}
              label={exporting ? "Exporting" : "Export"}
              info={exporting ? "Preparing file" : `${totalCount} transaction${totalCount === 1 ? "" : "s"}`}
              onClick={handleExport}
              disabled={totalCount === 0}
              loading={exporting}
            />
          }
        />

        <section className="stats">
          <MetricCard icon={<ReceiptLongIcon />} title="Total transactions" value={totalCount} />
          <MetricCard icon={<CheckCircleOutlineIcon />} title="Paid" value={counts.paid || 0} tone="success" />
          <MetricCard icon={<ReceiptLongIcon />} title="Unpaid" value={counts.unpaid || 0} tone="warning" />
          <MetricCard
            icon={<ReplayIcon />}
            title="Reversed"
            value={counts.reversed || 0}
            tone={counts.reversed > 0 ? "danger" : "default"}
          />
        </section>

        <section className="filters">
          <SearchField
            placeholder="Search by transaction reference..."
            value={refFilter}
            onChange={(e) => setRefFilter(e.target.value)}
          />
          <div className="filtersSelects">
            <FilterDropdown
              label={statusFilter ? `Status: ${STATUS_LABELS[statusFilter]}` : "Status"}
              options={[{ value: "", label: "All statuses" }, ...STATUS_OPTIONS]}
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            />
            <FilterDropdown
              label={reversedOnly ? "Reversed only" : "Reversed"}
              options={[
                { value: "", label: "All transactions" },
                { value: "reversed", label: "Reversed only" },
              ]}
              value={reversedOnly ? "reversed" : ""}
              onChange={(e) => setReversedOnly(e.target.value === "reversed")}
            />
            <NumberRangeFilter
              label="Amount"
              fromValue={amountFrom}
              toValue={amountTo}
              onFromChange={(e) => setAmountFrom(e.target.value)}
              onToChange={(e) => setAmountTo(e.target.value)}
            />
            <DateRangeFilter
              label="Created"
              fromValue={dateFrom}
              toValue={dateTo}
              onFromChange={(e) => setDateFrom(e.target.value)}
              onToChange={(e) => setDateTo(e.target.value)}
            />
            <DateRangeFilter
              label="Paid"
              fromValue={paidFrom}
              toValue={paidTo}
              onFromChange={(e) => setPaidFrom(e.target.value)}
              onToChange={(e) => setPaidTo(e.target.value)}
            />
            <FilterDropdown
              label={`Sort: ${SORT_OPTIONS.find((o) => o.value === sortOption).label}`}
              options={SORT_OPTIONS}
              value={sortOption}
              active={false}
              align="right"
              onChange={(e) => setSortOption(e.target.value)}
            />
          </div>
        </section>

        {activeFilterChips.length > 0 && (
          <ActiveFilterChipList filters={activeFilterChips} onClearFilter={clearFilter} onClearAll={clearAllFilters} />
        )}

        <div className="tableTopBar">
          <div className="tableTopBarLeft">
            {transactions.length > 0 && (
              <span className="financePageAmount">This page: ${pageAmount.toFixed(2)}</span>
            )}
          </div>
          <div className="tableTopBarRight">
            <IconButton
              className="tableTopIconButton"
              size="small"
              onClick={() => refetch()}
              title="Refresh transactions"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </div>
        </div>

        <div className={`tableResponsive ${loading && data ? "isRefetching" : ""}`}>
          {loading && data && <span className="financeRefetchSpinner" aria-hidden="true" />}
          <section className="table">
            <div className="tableHeader">
              <div className="tableHeaderCell">Transaction</div>
              <div className="tableHeaderCell">Status</div>
              <div className="tableHeaderCell">Amount</div>
              <div className="tableHeaderCell">Paid</div>
              <div className="tableHeaderCell">Created</div>
              <div className="tableHeaderCell tableHeaderAction">Actions</div>
            </div>

            {transactions.length === 0 ? (
              <EmptyState
                icon={<ReceiptLongIcon />}
                title={totalCount === 0 && activeFilterChips.length === 0 ? "No transactions yet" : "No matches"}
                message={
                  totalCount === 0 && activeFilterChips.length === 0
                    ? "Payout transactions will show up here once they're created."
                    : "No transactions match these filters. Try widening or clearing them."
                }
              />
            ) : (
              transactions.map((transaction) => (
                <div className="row" key={transaction._id}>
                  <div className="financeRefCell" data-label="Transaction">
                    <span className="financeRef">{transaction.transactionId || transaction._id}</span>
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => handleCopy(transaction.transactionId || transaction._id)}
                      title="Copy"
                    >
                      {copiedId === (transaction.transactionId || transaction._id) ? (
                        <CheckCircleOutlineIcon fontSize="small" />
                      ) : (
                        <FileCopyIcon style={{ fontSize: 16 }} />
                      )}
                    </IconButton>
                    {transaction.reversed && <StatusBadge status="danger" label="Reversed" dot={false} />}
                    <span className="financeSubtext">
                      {TYPE_LABELS[transaction.type] || transaction.type} · {transaction.payments.length} payment
                      {transaction.payments.length === 1 ? "" : "s"}
                    </span>
                  </div>

                  <span data-label="Status">
                    <StatusBadge
                      status={STATUS_TONES[transaction.status] || "neutral"}
                      label={STATUS_LABELS[transaction.status] || transaction.status}
                    />
                  </span>

                  <span className="financeAmount" data-label="Amount">
                    ${Math.abs(transaction.amount || 0).toFixed(2)}
                  </span>

                  <span className="updated" data-label="Paid">
                    {transaction.paidAt ? (
                      <React.Fragment>
                        <strong>{formatDate(transaction.paidAt)}</strong>
                        <span>{formatTime(transaction.paidAt)}</span>
                      </React.Fragment>
                    ) : (
                      <span className="financeRefMuted">Not yet</span>
                    )}
                  </span>

                  <span className="updated" data-label="Created">
                    <strong>{formatDate(transaction.createdAt)}</strong>
                    <span>{formatTime(transaction.createdAt)}</span>
                  </span>

                  <div className="actions">
                    <IconButton
                      size="small"
                      className="rowActionButton"
                      onClick={() => setTransactionDetail(transaction)}
                      title="View transaction"
                    >
                      <ChevronRightIcon fontSize="small" />
                    </IconButton>
                  </div>
                </div>
              ))
            )}
          </section>
        </div>

        {totalCount > 0 && (
          <PaginationComponent
            currentPage={pageCount}
            pageSize={perPage}
            totalCount={totalCount}
            pageSizeOptions={[10, 20, 50]}
            onPageChange={(newPage) => setPageCount(newPage)}
            onPageSizeChange={(size) => {
              setPerPage(parseInt(size, 10));
              setPageCount(0);
            }}
          />
        )}
      </main>

      <DialogShell
        open={Boolean(transactionDetail)}
        onClose={() => setTransactionDetail(null)}
        title="Transaction details"
      >
        {transactionDetail && <ShopTransactionDetails transaction={transactionDetail} />}
      </DialogShell>
    </div>
  );
};

export default Transactions;
